import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchWorld, fetchWorlds, updateWorld, createWorld } from "../../actions/mods_factory/worldsActions";

export interface WorldInterface {
  url: string;

  id: number;
  title: string;
  body: string;
  search_text: string;
  thumbnail_url: string;
  mcworld_url: string;
  category_ids: number[];
  likes_count: number;
  comments_count: number;
  popular_count: number;
  downloads_count: number;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: WorldInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<WorldInterface>();

const slice = createSlice({
  name: 'worlds',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchWorlds.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchWorlds.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.worlds;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchWorlds.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchWorld.fulfilled, (state, action) => {
        const world = Object.values(action.payload.world)[0];
        state.entities[world.id] = world;
      })

      // #create
      .addCase(createWorld.fulfilled, (state, action) => {
        const world = Object.values(action.payload.world)[0];
        state.entities[world.id] = world;
      })
      .addCase(createWorld.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateWorld.fulfilled, (state, action) => {
        const world = Object.values(action.payload.world)[0];
        state.entities[world.id] = world;
      })
      .addCase(updateWorld.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectWorldById,
  selectIds: selectWorldIds,
  selectEntities: selectWorldEntities,
  selectAll: selectAllWorlds,
  selectTotal: selectTotalWorlds
} = adaptor.getSelectors<RootState>(state => state.modsFactory.worlds);
